'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Code2, Headphones, Phone, Megaphone, Globe, Smartphone, Database, Cloud, Wrench, Zap, PhoneIncoming, Users, ClipboardList, BarChart3, Mic, PhoneCall, Radio, Search, Mail, Target, TrendingUp, LineChart, Share2, ArrowRight, MapPin, PhoneCall as PhoneIcon, Clock, CheckCircle2, Send, Star, Building2, Layers, LogIn, X, Eye, EyeOff, ShieldCheck, UserCircle2, Briefcase, Award, Globe2, Rocket } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


/* ─── Types ─────────────────────────────────────────────── */
interface ServiceCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  tags: string[];
  span?: boolean;
}

interface ServiceCategory {
  key: string;
  label: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
  cards: ServiceCard[];
  image: string;
  imageAlt: string;
}

type LoginRole = 'admin' | 'customer' | 'employee';

/* ─── Mock Credentials ───────────────────────────────────── */
const mockCredentials = [
{ role: 'admin' as LoginRole, email: 'ryan.ashford@arrows.io', password: 'Admin@TC2026', redirect: '/projects' },
{ role: 'employee' as LoginRole, email: 'kavya.sharma@arrows.io', password: 'Staff@TC2026', redirect: '/projects' },
{ role: 'customer' as LoginRole, email: 'james.okafor@pinnaclesys.com', password: 'Client@TC2026', redirect: '/client-portal' }];


/* ─── Data ───────────────────────────────────────────────── */
const stats = [
{ value: '500+', label: 'Projects Delivered' },
{ value: '120+', label: 'Active Clients' },
{ value: '18', label: 'Countries Served' },
{ value: '200+', label: 'Team Members' }];


const serviceCategories: ServiceCategory[] = [
{
  key: 'it',
  label: 'IT & Software',
  icon: Code2,
  color: 'text-primary',
  bg: 'bg-primary/10',
  border: 'border-primary/25',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_178fbac79-1772091570606.png",
  imageAlt: 'Software developer writing code on multiple monitors in a modern tech office',
  cards: [
  { id: 'it-1', title: 'Website & Web App Development', description: 'Performant websites and scalable web apps built for conversion and growth.', icon: Globe, tags: ['React', 'Next.js', 'Full-Stack'], span: true },
  { id: 'it-2', title: 'Mobile App Development', description: 'Native and cross-platform apps for iOS and Android.', icon: Smartphone, tags: ['Flutter', 'React Native'] },
  { id: 'it-3', title: 'ERP / CRM Solutions', description: 'Custom enterprise systems tailored to your workflows.', icon: Database, tags: ['ERP', 'CRM'] },
  { id: 'it-4', title: 'Cloud & Hosting', description: 'Managed cloud infrastructure on AWS, GCP, or Azure.', icon: Cloud, tags: ['AWS', 'GCP'] },
  { id: 'it-5', title: 'API & Integrations', description: 'Connect your tools with reliable, documented API integrations.', icon: Zap, tags: ['REST', 'GraphQL'] },
  { id: 'it-6', title: 'IT Support & Maintenance', description: 'Ongoing monitoring, support, and proactive system maintenance.', icon: Wrench, tags: ['24/7', 'SLA'] }]

},
{
  key: 'bpo',
  label: 'BPO & Call Center',
  icon: Headphones,
  color: 'text-cyan',
  bg: 'bg-cyan/10',
  border: 'border-cyan/25',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa3d9798-1768554124735.png",
  imageAlt: 'Professional call center agents wearing headsets working at their desks in a modern BPO office',
  cards: [
  { id: 'bpo-1', title: 'Inbound & Outbound Calling', description: 'Professional agents managing customer inquiries, sales, and follow-ups.', icon: PhoneIncoming, tags: ['24/7', 'Multi-language'], span: true },
  { id: 'bpo-2', title: 'Customer & Technical Support', description: 'Omnichannel support via phone, email, chat, and social media.', icon: Users, tags: ['Omnichannel', 'Tier 1 & 2'] },
  { id: 'bpo-3', title: 'Lead Generation', description: 'Qualified lead pipelines through targeted research and outreach.', icon: Target, tags: ['B2B', 'B2C'] },
  { id: 'bpo-4', title: 'Data Entry & Processing', description: 'Accurate, high-volume data entry, cleansing, and processing.', icon: ClipboardList, tags: ['Accuracy', 'High Volume'] },
  { id: 'bpo-5', title: 'Back-Office Operations', description: 'Administrative support including document processing and compliance.', icon: BarChart3, tags: ['Admin', 'Compliance'] },
  { id: 'bpo-6', title: 'Workforce Management', description: 'Agent scheduling, performance tracking, and call center optimization.', icon: Users, tags: ['WFM', 'Scheduling'] }]

},
{
  key: 'voip',
  label: 'VOIP Platform',
  icon: Phone,
  color: 'text-warning',
  bg: 'bg-warning/10',
  border: 'border-warning/25',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3bedfad-1775833676359.png",
  imageAlt: 'Modern VOIP phone system and communication technology equipment on a desk',
  cards: [
  { id: 'voip-1', title: 'SIP / VOIP Calling', description: 'Crystal-clear SIP-based calling with global coverage and low latency.', icon: PhoneCall, tags: ['SIP', 'HD Voice', 'Global'], span: true },
  { id: 'voip-2', title: 'IVR & Call Routing', description: 'Intelligent IVR menus and skill-based routing for efficient call handling.', icon: Radio, tags: ['IVR', 'ACD'] },
  { id: 'voip-3', title: 'Call Recording & Compliance', description: 'Automatic recording with secure cloud storage and compliance controls.', icon: Mic, tags: ['Compliance', 'Cloud'] },
  { id: 'voip-4', title: 'Call Queues & Campaigns', description: 'Manage inbound queues and outbound dialer campaigns from one dashboard.', icon: PhoneIncoming, tags: ['Predictive Dialer'] },
  { id: 'voip-5', title: 'Real-Time Dashboards', description: 'Live call monitoring, agent status boards, and supervisor controls.', icon: BarChart3, tags: ['Live Monitor'] },
  { id: 'voip-6', title: 'Reports & Analytics', description: 'Detailed call reports, agent performance metrics, and trend analytics.', icon: LineChart, tags: ['CDR', 'KPIs'] }]

},
{
  key: 'marketing',
  label: 'Digital Marketing',
  icon: Megaphone,
  color: 'text-success',
  bg: 'bg-success/10',
  border: 'border-success/25',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15fbd14c4-1766488541955.png",
  imageAlt: 'Digital marketing analytics dashboard showing charts and social media campaign performance metrics',
  cards: [
  { id: 'mkt-1', title: 'SEO & Content Marketing', description: 'Technical SEO, content strategy, and link building to rank for the terms that matter.', icon: Search, tags: ['On-Page', 'Technical', 'Content'], span: true },
  { id: 'mkt-2', title: 'Google & Social Ads', description: 'PPC and paid social campaigns on Google, Meta, LinkedIn, and TikTok.', icon: Target, tags: ['PPC', 'Meta Ads'] },
  { id: 'mkt-3', title: 'Social Media Management', description: 'Organic social strategy, content creation, and community management.', icon: Share2, tags: ['LinkedIn', 'Meta'] },
  { id: 'mkt-4', title: 'Email Marketing', description: 'Automated sequences, newsletters, and drip campaigns that convert.', icon: Mail, tags: ['Automation', 'Drip'] },
  { id: 'mkt-5', title: 'Lead Generation Funnels', description: 'Multi-channel funnels designed to fill your sales pipeline consistently.', icon: TrendingUp, tags: ['Funnels', 'Landing Pages'] },
  { id: 'mkt-6', title: 'Analytics & Reporting', description: 'GA4, Tag Manager, and custom dashboards to track every marketing dollar.', icon: LineChart, tags: ['GA4', 'GTM'] }]

}];


const testimonials = [
{
  id: 't1',
  name: 'Sarah Mitchell',
  role: 'CEO, Pinnacle Systems',
  rating: 5,
  quote: 'Arrows Groups delivered our ERP system 2 weeks ahead of schedule. Their team understood our workflows better than we did.',
  initials: 'SM',
  color: 'bg-primary/20 text-primary',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb18e0a9-1763297020873.png",
  avatarAlt: 'Sarah Mitchell, CEO of Pinnacle Systems, professional headshot'
},
{
  id: 't2',
  name: 'James Okafor',
  role: 'Operations Director, NovaCorp',
  rating: 5,
  quote: 'The BPO team handles 800+ calls daily with consistent quality. Our customer satisfaction scores went up 34% in 3 months.',
  initials: 'JO',
  color: 'bg-cyan/20 text-cyan',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14aebd019-1763293570235.png",
  avatarAlt: 'James Okafor, Operations Director at NovaCorp, professional headshot'
},
{
  id: 't3',
  name: 'Priya Nair',
  role: 'Marketing Head, Elevate Digital',
  rating: 5,
  quote: 'Our organic traffic tripled in 6 months. The SEO and content team at Arrows Groups genuinely knows what they are doing.',
  initials: 'PN',
  color: 'bg-success/20 text-success',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbc7f990-1763296516493.png",
  avatarAlt: 'Priya Nair, Marketing Head at Elevate Digital, professional headshot'
}];


const whyUsItems = [
{
  icon: Award,
  title: 'Proven Track Record',
  description: '500+ projects delivered on time across 18 countries with a 96% client retention rate.',
  color: 'text-primary',
  bg: 'bg-primary/10'
},
{
  icon: Globe2,
  title: 'Global Reach, Local Expertise',
  description: 'Teams in UAE, South Asia, and Europe — giving you round-the-clock coverage and cultural fluency.',
  color: 'text-cyan',
  bg: 'bg-cyan/10'
},
{
  icon: Rocket,
  title: 'End-to-End Delivery',
  description: 'From strategy to deployment and ongoing support — one partner for your entire technology stack.',
  color: 'text-success',
  bg: 'bg-success/10'
},
{
  icon: ShieldCheck,
  title: 'Enterprise-Grade Security',
  description: 'ISO-aligned processes, NDA-protected engagements, and secure cloud infrastructure as standard.',
  color: 'text-warning',
  bg: 'bg-warning/10'
}];


const roleConfig: Record<LoginRole, {label: string;icon: React.ElementType;color: string;bg: string;border: string;description: string;}> = {
  admin: {
    label: 'Admin',
    icon: ShieldCheck,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
    description: 'Full platform access & management'
  },
  customer: {
    label: 'Customer',
    icon: UserCircle2,
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    description: 'View projects, invoices & support'
  },
  employee: {
    label: 'Employee',
    icon: Briefcase,
    color: 'text-cyan',
    bg: 'bg-cyan/10',
    border: 'border-cyan/30',
    description: 'Access your tasks & team tools'
  }
};

/* ─── Login Modal ────────────────────────────────────────── */
function LoginModal({ onClose }: {onClose: () => void;}) {
  const router = useRouter();
  const [selectedRole, setSelectedRole] = useState<LoginRole>('customer');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const cfg = roleConfig[selectedRole];
  const RoleIcon = cfg.icon;

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    await new Promise((r) => setTimeout(r, 900));

    const match = mockCredentials.find(
      (c) => c.role === selectedRole && c.email === email && c.password === password
    );

    if (!match) {
      setError('Invalid email or password. Use the demo credentials below.');
      setLoading(false);
      return;
    }

    setLoading(false);
    router.push(match.redirect);
    onClose();
  }

  const demoCred = mockCredentials.find((c) => c.role === selectedRole);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose} />
      

      {/* Modal */}
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
              <Layers size={15} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Arrows Groups</p>
              <p className="text-xs text-muted-foreground">arrowsgroups.com</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5">
          <h2 className="text-lg font-bold text-foreground mb-1">Sign in to your account</h2>
          <p className="text-xs text-muted-foreground mb-5">Select your role to access the right portal</p>

          {/* Role selector */}
          <div className="grid grid-cols-3 gap-2.5 mb-5">
            {(Object.keys(roleConfig) as LoginRole[]).map((role) => {
              const rc = roleConfig[role];
              const Icon = rc.icon;
              const isActive = selectedRole === role;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => {setSelectedRole(role);setError('');setEmail('');setPassword('');}}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all duration-150 ${
                  isActive ?
                  `${rc.bg} ${rc.border} ${rc.color}` :
                  'bg-secondary/40 border-border text-muted-foreground hover:border-border/80 hover:text-foreground'}`
                  }>
                  
                  <Icon size={20} />
                  <span className="text-xs font-semibold">{rc.label}</span>
                </button>);

            })}
          </div>

          {/* Role description */}
          <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg mb-5 ${cfg.bg} border ${cfg.border}`}>
            <RoleIcon size={15} className={cfg.color} />
            <p className={`text-xs font-medium ${cfg.color}`}>{cfg.description}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@arrowsgroups.com"
                className="w-full px-3.5 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all" />
              
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••"
                  style={{ color: 'var(--foreground)', WebkitTextFillColor: 'var(--foreground)' }}
                  className="w-full px-3.5 py-2.5 pr-10 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all" />
                
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error &&
            <p className="text-xs text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2">{error}</p>
            }

            <button
              type="submit"
              disabled={loading}
              className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-150 ${cfg.bg} ${cfg.color} border ${cfg.border} hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed`}>
              
              {loading ?
              <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                  </svg>
                  Signing in…
                </> :

              <>
                  <LogIn size={15} />
                  Sign in as {cfg.label}
                </>
              }
            </button>
          </form>

          {/* Demo credentials hint */}
          {demoCred &&
          <div className="mt-4 p-3 bg-secondary/50 border border-border rounded-lg">
              <p className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Demo credentials</p>
              <div className="flex flex-col gap-0.5">
                <button
                type="button"
                onClick={() => {setEmail(demoCred.email);setPassword(demoCred.password);}}
                className="text-left text-xs text-muted-foreground hover:text-foreground transition-colors">
                
                  <span className="font-mono">{demoCred.email}</span>
                </button>
                <span className="text-xs text-muted-foreground font-mono">{demoCred.password}</span>
                <p className="text-2xs text-muted-foreground/60 mt-1">Click email to autofill</p>
              </div>
            </div>
          }
        </div>
      </div>
    </div>);

}

/* ─── Main Page ──────────────────────────────────────────── */
export default function ArrowsHomePage() {
  const [activeCategory, setActiveCategory] = useState<string>('it');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    rating: 0
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const current = serviceCategories.find((c) => c.key === activeCategory) ?? serviceCategories[0];
  const CatIcon = current.icon;

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Login Modal ──────────────────────────────────────── */}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}

      {/* ── Top Nav ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
              <Layers size={16} className="text-primary" />
            </div>
            <span className="font-bold text-foreground text-base sm:text-lg tracking-tight">Arrows Groups</span>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {['Services', 'About', 'Contact'].map((item) =>
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/60 transition-colors">
              
                {item}
              </a>
            )}
            <Link
              href="/gallery"
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-secondary">

              Gallery
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLoginModal(true)}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-secondary border border-border text-foreground rounded-lg text-sm font-medium hover:border-primary/40 hover:text-primary transition-colors">
              
              <LogIn size={14} />
              <span className="hidden sm:inline">Login</span>
            </button>
            <Link href="/client-portal/request-service">
              <button className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                <span className="hidden sm:inline">Get a Quote</span>
                <span className="sm:hidden">Quote</span>
                <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] blob-primary opacity-50" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 blob-accent opacity-35" />
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-warning/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 py-12 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-5">
                <Building2 size={12} />
                IT · BPO · VOIP · Digital Marketing
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] font-bold text-foreground leading-[1.1] mb-5">
                Your one-stop<br />
                <span className="text-primary">technology &</span><br />
                outsourcing partner
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                Arrows Groups delivers end-to-end IT solutions, BPO operations, enterprise VOIP infrastructure, and performance-driven digital marketing — all under one roof.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <Link href="/client-portal/request-service">
                  <button className="flex items-center gap-2 px-5 sm:px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-all duration-200 hover:shadow-lg hover:shadow-primary/20">
                    Start a Project <ArrowRight size={16} />
                  </button>
                </Link>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-card border border-border text-foreground rounded-xl font-semibold hover:border-primary/40 transition-all duration-200">
                  
                  <LogIn size={16} />
                  Sign In
                </button>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap gap-8">
                {stats.map((s) =>
                <div key={s.label} className="flex flex-col">
                    <span className="text-2xl font-bold text-foreground font-tabular">{s.value}</span>
                    <span className="text-xs text-muted-foreground mt-0.5">{s.label}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl shadow-black/30">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_15ee5373f-1764645268112.png"
                  alt="Modern technology office with professionals collaborating on software projects and digital solutions"
                  width={900}
                  height={600}
                  className="w-full h-[420px] object-cover"
                  priority />
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                {/* Floating stat card */}
                <div className="absolute bottom-5 left-5 bg-card/90 backdrop-blur-md border border-border rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-success/20 border border-success/30 flex items-center justify-center">
                    <CheckCircle2 size={18} className="text-success" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">96% Client Retention</p>
                    <p className="text-2xs text-muted-foreground">Across all service lines</p>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute top-5 right-5 bg-primary/90 backdrop-blur-md rounded-xl px-3 py-2">
                  <p className="text-xs font-bold text-white">18 Countries</p>
                </div>
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/5 rounded-full blur-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Login CTA Banner ─────────────────────────────────── */}
      <section className="border-b border-border bg-card/40">
        <div className="max-w-screen-xl mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-base font-bold text-foreground mb-1">Already a client or team member?</h3>
              <p className="text-sm text-muted-foreground">Sign in to access your portal — projects, invoices, support, and more.</p>
            </div>
            <div className="flex flex-wrap gap-3 flex-shrink-0">
              {(Object.keys(roleConfig) as LoginRole[]).map((role) => {
                const rc = roleConfig[role];
                const RIcon = rc.icon;
                return (
                  <button
                    key={role}
                    onClick={() => setShowLoginModal(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all duration-150 ${rc.bg} ${rc.border} ${rc.color} hover:opacity-80`}>
                    
                    <RIcon size={15} />
                    {rc.label} Login
                  </button>);

              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────────── */}
      <section id="services" className="max-w-screen-xl mx-auto px-6 py-16">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">What We Do</p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Services built for scale</h2>
          <p className="text-sm text-muted-foreground max-w-xl">
            From a single website to a full outsourced operation — pick the services your business needs and we handle the rest.
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {serviceCategories.map((cat) => {
            const CIcon = cat.icon;
            const isActive = activeCategory === cat.key;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all duration-150 ${
                isActive ?
                `${cat.bg} ${cat.color} ${cat.border}` :
                'bg-card border-border text-muted-foreground hover:text-foreground'}`
                }>
                
                <CIcon size={15} />
                {cat.label}
              </button>);

          })}
        </div>

        {/* Category header card with image */}
        <div className={`card-surface mb-6 border ${current.border} relative overflow-hidden`}>
          <div className="grid grid-cols-1 md:grid-cols-3">
            {/* Image panel */}
            <div className="relative h-48 md:h-auto md:col-span-1 overflow-hidden">
              <Image
                src={current.image}
                alt={current.imageAlt}
                width={400}
                height={300}
                className="w-full h-full object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/40 md:bg-gradient-to-l" />
            </div>
            {/* Content panel */}
            <div className="p-6 md:col-span-2 flex flex-col justify-center">
              <div className="absolute top-0 right-0 w-48 h-48 opacity-[0.04] pointer-events-none">
                <CatIcon size={192} />
              </div>
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-3 ${current.bg} ${current.color}`}>
                    <CatIcon size={12} />
                    {current.label}
                  </div>
                  <p className="text-sm text-muted-foreground max-w-xl">
                    {current.key === 'it' && 'End-to-end software and technology solutions — from concept to deployment.'}
                    {current.key === 'bpo' && 'Outsourced operations that scale with your business — dedicated teams, consistent quality.'}
                    {current.key === 'voip' && 'Enterprise-grade VOIP infrastructure with real-time analytics and full call-center capabilities.'}
                    {current.key === 'marketing' && 'Data-driven marketing strategies that generate qualified traffic and measurable ROI.'}
                  </p>
                </div>
                <Link href="/client-portal/request-service">
                  <button className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap flex-shrink-0">
                    Request This Service <ArrowRight size={14} />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bento service grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {current.cards.map((card) => {
            const SvcIcon = card.icon;
            return (
              <div
                key={card.id}
                className={`card-surface p-5 group hover:border-primary/30 transition-all duration-200 ${
                card.span ? 'lg:col-span-2' : ''}`
                }>
                
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${current.bg} ${current.color} group-hover:scale-105 transition-transform duration-200`}>
                  <SvcIcon size={20} />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1.5">{card.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{card.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {card.tags.map((tag) =>
                  <span key={tag} className={`text-2xs px-2 py-0.5 rounded-full font-medium ${current.bg} ${current.color}`}>
                      {tag}
                    </span>
                  )}
                </div>
              </div>);

          })}
        </div>
      </section>

      {/* ── Visual Industry Showcase ──────────────────────────── */}
      <section id="about" className="border-t border-border bg-card/20">
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Our Expertise</p>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Built for every industry</h2>
            <p className="text-sm text-muted-foreground max-w-xl">
              We've partnered with businesses across finance, healthcare, retail, logistics, and tech — delivering solutions that fit each sector's unique demands.
            </p>
          </div>

          {/* Asymmetric image bento */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-12">
            {/* Large left image */}
            <div className="md:col-span-7 relative rounded-2xl overflow-hidden border border-border group">
              <Image
                src="https://img.rocket.new/generatedImages/rocket_gen_img_171170bd3-1764687877901.png"
                alt="Diverse professional team collaborating around a conference table in a modern corporate office"
                width={900}
                height={500}
                className="w-full h-64 md:h-80 object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/10 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-sm font-bold text-white mb-1">Global Teams, Local Impact</p>
                <p className="text-xs text-white/70">200+ professionals across 3 continents</p>
              </div>
            </div>

            {/* Right column: two stacked images */}
            <div className="md:col-span-5 flex flex-col gap-4">
              <div className="relative rounded-2xl overflow-hidden border border-border group flex-1">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_10abc6936-1773159882220.png"
                  alt="Software engineers reviewing code on laptops in a modern startup office environment"
                  width={600}
                  height={240}
                  className="w-full h-36 object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <p className="text-xs font-bold text-white">IT & Software</p>
                </div>
                <div className="absolute top-3 right-3 bg-primary/80 rounded-lg px-2 py-1">
                  <p className="text-2xs font-semibold text-white">500+ Projects</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden border border-border group flex-1">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_15d22dba0-1772902168440.png"
                  alt="Business analytics dashboard on a laptop screen showing marketing performance data and charts"
                  width={600}
                  height={240}
                  className="w-full h-36 object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <p className="text-xs font-bold text-white">Digital Marketing</p>
                </div>
                <div className="absolute top-3 right-3 bg-success/80 rounded-lg px-2 py-1">
                  <p className="text-2xs font-semibold text-white">3× Avg. ROI</p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Us grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {whyUsItems.map((item) => {
              const ItemIcon = item.icon;
              return (
                <div key={item.title} className="card-surface p-5 flex flex-col gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.bg}`}>
                    <ItemIcon size={20} className={item.color} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* ── Gallery ──────────────────────────────────────────── */}
      <section className="border-t border-border">
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Our Work</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Project Gallery</h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-xl">
                A curated look at the work we've delivered — from enterprise software to global BPO operations and digital campaigns.
              </p>
            </div>
            <Link
              href="/gallery"
              className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors">

              View all projects <ArrowRight size={16} />
            </Link>
          </div>

          {/* Gallery Grid — 6 featured items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[200px]">
            {[
            { id: 'g1', title: 'Web Platform Launch', subtitle: 'Full-stack e-commerce solution for NovaCorp', category: 'it', image: "https://img.rocket.new/generatedImages/rocket_gen_img_178fbac79-1772091570606.png", alt: 'Software developer writing code on multiple monitors in a modern tech office', span: 'wide', tags: ['React', 'Next.js'] },
            { id: 'g2', title: 'BPO Operations Center', subtitle: 'State-of-the-art call center facility in Dubai', category: 'bpo', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa3d9798-1768554124735.png", alt: 'Professional call center agents wearing headsets working at their desks in a modern BPO office', span: 'tall', tags: ['BPO', '24/7'] },
            { id: 'g3', title: 'VOIP Infrastructure', subtitle: 'Enterprise-grade SIP telephony rollout', category: 'voip', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3bedfad-1775833676359.png", alt: 'Modern VOIP phone system and communication technology equipment on a desk', tags: ['SIP', 'HD Voice'] },
            { id: 'g4', title: 'Digital Marketing Campaign', subtitle: 'Multi-channel campaign — 3× organic growth', category: 'marketing', image: "https://img.rocket.new/generatedImages/rocket_gen_img_186ddb7d0-1772192912089.png", alt: 'Digital marketing analytics dashboard showing charts and social media campaign performance metrics', tags: ['SEO', 'PPC'] },
            { id: 'g5', title: 'ERP System Delivery', subtitle: 'Custom ERP — 2 weeks ahead of schedule', category: 'it', image: "https://img.rocket.new/generatedImages/rocket_gen_img_18bd71bed-1787245745790.png", alt: 'Enterprise resource planning software interface showing production and inventory management modules', span: 'wide', tags: ['ERP', 'Custom Dev'] },
            { id: 'g6', title: 'Lead Generation Funnel', subtitle: 'B2B funnel delivering 400+ qualified leads/month', category: 'marketing', image: 'https://img.rocket.new/generatedImages/rocket_gen_img_186ddb7d0-1772192912089.png', alt: 'Marketing funnel analytics showing lead conversion rates and campaign performance metrics', tags: ['Lead Gen', 'B2B'] }].
            map((item) => {
              const catColors: Record<string, {text: string;bg: string;border: string;}> = {
                it: { text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30' },
                bpo: { text: 'text-cyan', bg: 'bg-cyan/10', border: 'border-cyan/30' },
                voip: { text: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' },
                marketing: { text: 'text-success', bg: 'bg-success/10', border: 'border-success/30' }
              };
              const colors = catColors[item.category] || catColors.it;
              const spanClass = item.span === 'wide' ? 'col-span-2' : item.span === 'tall' ? 'row-span-2' : '';
              const catLabel: Record<string, string> = { it: 'IT & Software', bpo: 'BPO', voip: 'VOIP', marketing: 'Marketing' };
              return (
                <Link
                  key={item.id}
                  href="/gallery"
                  className={`group relative overflow-hidden rounded-xl border border-border bg-card ${spanClass}`}>

                  <img
                    src={item.image}
                    alt={item.alt}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border backdrop-blur-sm ${colors.bg} ${colors.text} ${colors.border}`}>
                      {catLabel[item.category]}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-sm font-semibold text-white leading-snug">{item.title}</h3>
                    <p className="text-xs text-white/70 mt-0.5 line-clamp-1">{item.subtitle}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.map((tag) =>
                      <span key={tag} className="text-2xs px-1.5 py-0.5 rounded bg-white/10 text-white/80 border border-white/20">{tag}</span>
                      )}
                    </div>
                  </div>
                </Link>);

            })}
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────── */}
      <section className="border-t border-border">
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Client Stories</p>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">What our clients say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t) =>
            <div key={t.id} className="card-surface p-6 flex flex-col gap-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) =>
                <Star key={i} size={14} className={i < t.rating ? 'text-warning fill-warning' : 'text-muted-foreground'} />
                )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-border flex-shrink-0">
                    <Image
                    src={t.avatar}
                    alt={t.avatarAlt}
                    width={40}
                    height={40}
                    className="w-full h-full object-cover" />
                  
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Contact + Feedback Form ───────────────────────────── */}
      <section id="contact" className="border-t border-border">
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Get In Touch</p>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Contact & Feedback</h2>
            <p className="text-sm text-muted-foreground max-w-xl">
              Have a project in mind, a question about our services, or feedback to share? We respond within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact info panel */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              {/* Office image */}
              <div className="relative rounded-xl overflow-hidden border border-border">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_14c4de883-1786638688906.png"
                  alt="Arrows Groups headquarters building in Business Bay Dubai with modern glass facade"
                  width={600}
                  height={200}
                  className="w-full h-40 object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-background/70 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <p className="text-xs font-bold text-white">Business Bay, Dubai</p>
                  <p className="text-2xs text-white/70">Global HQ</p>
                </div>
              </div>

              <div className="card-surface p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Office Address</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Arrows Groups HQ<br />
                    Business Bay, Dubai, UAE<br />
                    & Remote Teams Worldwide
                  </p>
                </div>
              </div>

              <div className="card-surface p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-cyan/10 border border-cyan/20 flex items-center justify-center flex-shrink-0">
                  <PhoneIcon size={18} className="text-cyan" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Phone & WhatsApp</p>
                  <p className="text-xs text-muted-foreground">+971 4 000 0000</p>
                  <p className="text-xs text-muted-foreground">+1 (800) 000-0000 (US)</p>
                </div>
              </div>

              <div className="card-surface p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-success/10 border border-success/20 flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-success" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Email</p>
                  <p className="text-xs text-muted-foreground">info@arrowsgroups.com</p>
                  <p className="text-xs text-muted-foreground">support@arrowsgroups.com</p>
                </div>
              </div>

              <div className="card-surface p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-warning/10 border border-warning/20 flex items-center justify-center flex-shrink-0">
                  <Clock size={18} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Business Hours</p>
                  <p className="text-xs text-muted-foreground">Mon – Fri: 9:00 AM – 7:00 PM GST</p>
                  <p className="text-xs text-muted-foreground">Support: 24 / 7 for active clients</p>
                </div>
              </div>
            </div>

            {/* Feedback / Contact form */}
            <div className="lg:col-span-3">
              <div className="card-surface p-6 md:p-8">
                {submitted ?
                <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-success/10 border border-success/20 flex items-center justify-center">
                      <CheckCircle2 size={32} className="text-success" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">Message Sent!</h3>
                    <p className="text-sm text-muted-foreground max-w-xs">
                      Thank you for reaching out. Our team will get back to you within 24 hours.
                    </p>
                    <button
                    onClick={() => {setSubmitted(false);setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '', rating: 0 });}}
                    className="mt-2 px-5 py-2 bg-card border border-border text-sm text-foreground rounded-lg hover:border-primary/40 transition-colors">
                    
                      Send Another Message
                    </button>
                  </div> :

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <div>
                      <h3 className="text-base font-bold text-foreground mb-1">Send us a message</h3>
                      <p className="text-xs text-muted-foreground">Fill in the form and we'll be in touch shortly.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Full Name *</label>
                        <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                        className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors" />
                      
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Email Address *</label>
                        <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors" />
                      
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Phone Number</label>
                        <input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors" />
                      
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Company</label>
                        <input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Ltd."
                        className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors" />
                      
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Service of Interest</label>
                      <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors">
                      
                        <option value="">Select a service...</option>
                        <option value="it">IT & Software Development</option>
                        <option value="bpo">BPO & Call Center</option>
                        <option value="voip">VOIP Platform</option>
                        <option value="marketing">Digital Marketing</option>
                        <option value="multiple">Multiple Services</option>
                        <option value="other">Other / General Inquiry</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Message *</label>
                      <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Tell us about your project, requirements, or feedback..."
                      className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none" />
                    
                    </div>

                    {/* Star rating */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-muted-foreground">Rate Your Experience (optional)</label>
                      <div className="flex gap-1">
                        {Array.from({ length: 5 }).map((_, i) => {
                        const filled = i < (hoverRating || formData.rating);
                        return (
                          <button
                            key={i}
                            type="button"
                            onMouseEnter={() => setHoverRating(i + 1)}
                            onMouseLeave={() => setHoverRating(0)}
                            onClick={() => setFormData((prev) => ({ ...prev, rating: i + 1 }))}
                            className="transition-transform hover:scale-110">
                            
                              <Star
                              size={22}
                              className={filled ? 'text-warning fill-warning' : 'text-muted-foreground'} />
                            
                            </button>);

                      })}
                        {formData.rating > 0 &&
                      <span className="ml-2 text-xs text-muted-foreground self-center">
                            {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][formData.rating]}
                          </span>
                      }
                      </div>
                    </div>

                    <button
                    type="submit"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all duration-200 hover:shadow-lg hover:shadow-primary/20 mt-1">
                    
                      <Send size={15} />
                      Send Message
                    </button>
                  </form>
                }
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="border-t border-border bg-card/20">
        <div className="max-w-screen-xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary/20 border border-primary/30 flex items-center justify-center">
              <Layers size={12} className="text-primary" />
            </div>
            <span className="text-sm font-semibold text-foreground">Arrows Groups</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            © 2025 Arrows Groups. All rights reserved. · info@arrowsgroups.com
          </p>
          <div className="flex gap-4">
            <a href="#services" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Services</a>
            <a href="#contact" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Contact</a>
            <button
              onClick={() => setShowLoginModal(true)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              
              Login
            </button>
          </div>
        </div>
      </footer>
    </div>);

}